import flatten from "lodash-es/flatten";
import { all, call, put, takeLatest } from "redux-saga/effects";

import {
    STROLLER_REVIEW_PHOTO_FETCH_TRIGGER,
    StrollerReviewPhotoFetchNormalizedResponse,
    StrollerReviewPhotoFetchTriggerAction,
    strollerReviewPhotoFetchApi,
} from "@/mk2/apps/strollers/containers/StrollerReviewPhoto/StrollerReviewPhoto.actions";
import {
    XHRAction,
    XHRError,
    XHRGetError,
    handleXHRGetErrorSaga,
    normalizeError,
} from "@/mk2/helpers/api";
import { getLogger } from "@/mk2/logger";
import {
    StrollerUserReviewEntity,
    StrollerUserReviewPhotoEntity,
    StrollerUserReviewSchema,
} from "@/mk2/schemas";
import { getDenormalizedEntities } from "@/mk2/selectors";
import { strollers_api_v2_reviews_photos_get_url } from "@/mk/urls/functions";

const logger = getLogger("strollers/StrollerReviewPhoto.sagas");

interface FetchApiResponse {
    body: StrollerReviewPhotoFetchNormalizedResponse;
}

function* fetchStrollerReviews({
    strollerSlug,
    reviewId,
    photoId,
    xhr,
}: StrollerReviewPhotoFetchTriggerAction & XHRAction) {
    yield put(strollerReviewPhotoFetchApi.request(strollerSlug, reviewId, photoId));

    try {
        const response: FetchApiResponse = yield call(() =>
            xhr.get(strollers_api_v2_reviews_photos_get_url({ strollerSlug, reviewId })),
        );

        const reviewPhotos: StrollerUserReviewPhotoEntity[] = flatten(
            getDenormalizedEntities<StrollerUserReviewEntity>(
                response.body,
                StrollerUserReviewSchema,
                response.body.result.strollerReviews,
            ).map((review) => review.photos),
        );
        const reviewPhoto = reviewPhotos.find((p) => p.photoFile.id === photoId);
        if (!reviewPhoto) {
            const err = new Error(
                "Photo not found",
            ) as XHRError<StrollerReviewPhotoFetchNormalizedResponse>;
            err.status = 404;
            err.response = response;
            throw err;
        }

        yield put(
            strollerReviewPhotoFetchApi.success(strollerSlug, reviewId, photoId, response.body),
        );
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, logger);
        yield put(
            strollerReviewPhotoFetchApi.failure(
                strollerSlug,
                reviewId,
                photoId,
                normalizeError(error),
            ),
        );
    }
}

export default function* root() {
    yield all([takeLatest(STROLLER_REVIEW_PHOTO_FETCH_TRIGGER, fetchStrollerReviews)]);
}
